import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Alert,
} from '@mui/material';
import { PlayArrow as PlayIcon, Replay as ReplayIcon } from '@mui/icons-material';
import { TOURS, TourDefinition, TourProgressMap } from '../tours/tourDefinitions';

interface Props {
  open: boolean;
  onClose: () => void;
  onSelectTour: (tour: TourDefinition, startStep?: number) => void;
  userId: number;
}

const STORAGE_KEY = (userId: number) => `studystride_tour_progress_${userId}`;

const TourSelectionModal: React.FC<Props> = ({ open, onClose, onSelectTour, userId }) => {
  const [progressMap, setProgressMap] = useState<TourProgressMap>({});
  const [resumeTarget, setResumeTarget] = useState<TourDefinition | null>(null);

  useEffect(() => {
    if (open) {
      try {
        const raw = localStorage.getItem(STORAGE_KEY(userId));
        setProgressMap(raw ? JSON.parse(raw) : {});
      } catch {
        setProgressMap({});
      }
    }
  }, [open, userId]);

  const handleSelectTour = (tour: TourDefinition) => {
    const progress = progressMap[tour.id];
    if (progress && !progress.completed && progress.lastStep > 0) {
      setResumeTarget(tour);
    } else {
      onSelectTour(tour);
    }
  };

  const handleResume = () => {
    if (!resumeTarget) return;
    const lastStep = progressMap[resumeTarget.id]?.lastStep ?? 0;
    setResumeTarget(null);
    onSelectTour(resumeTarget, lastStep);
  };

  const handleRestart = () => {
    if (!resumeTarget) return;
    setResumeTarget(null);
    onSelectTour(resumeTarget, 0);
  };

  return (
    <>
      <Dialog open={open && !resumeTarget} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>How It Works — Feature Tours</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select a tour to walk through that feature step-by-step on the real application.
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: 2,
            }}
          >
            {TOURS.map((tour) => {
              const progress = progressMap[tour.id];
              const isCompleted = progress?.completed;
              const isInProgress = progress && !progress.completed && progress.lastStep > 0;
              return (
                <Card key={tour.id} variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {tour.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {tour.description}
                    </Typography>
                    {isCompleted && (
                      <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                        ✓ Completed
                      </Typography>
                    )}
                    {isInProgress && (
                      <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                        ↩ In progress — step {(progress.lastStep ?? 0) + 1} of {tour.steps.length}
                      </Typography>
                    )}
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={isCompleted ? <ReplayIcon /> : <PlayIcon />}
                      onClick={() => handleSelectTour(tour)}
                    >
                      {isCompleted ? 'Replay' : 'Start Tour'}
                    </Button>
                  </CardActions>
                </Card>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Resume / Restart prompt */}
      <Dialog open={Boolean(resumeTarget)} onClose={() => setResumeTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Resume Tour?</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 1 }}>
            You left off at step {(progressMap[resumeTarget?.id ?? '']?.lastStep ?? 0) + 1} of{' '}
            {resumeTarget?.steps.length}.
          </Alert>
          <Typography variant="body2">Would you like to resume where you left off?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRestart}>Start Over</Button>
          <Button variant="contained" onClick={handleResume}>
            Resume
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TourSelectionModal;
